import {
  HOME_DEPOT_SETUP_OR_SKIP_ROUTE,
  HOME_DEPOT_SETUP_WEB,
  HOME_ROUTE,
  PARTNERS_ROUTE,
} from '../routes/namedRoutes';

export const USER_INITIATED_HD_ENROLLMENT_PARAM = 'userInitiatedHdEnrollment';
// Tells the Home Depot partner profile page (/partners/homedepot) to auto-open the
// enrollment modal, so the "Add rewards" CTA on the partners list and on the profile
// page itself lead to the same modal experience.
export const HD_ENROLL_MODAL_PARAM = 'openHdEnrollModal';

const homeDepotSetupRoutes = new Set([
  HOME_DEPOT_SETUP_OR_SKIP_ROUTE,
  HOME_DEPOT_SETUP_WEB,
]);
const localPathBaseUrl = 'https://optimus.local';

function getUserInitiatedHdEnrollmentFromState(state: unknown) {
  return Boolean(
    state &&
    typeof state === 'object' &&
    (state as { userInitiatedHdEnrollment?: unknown }).userInitiatedHdEnrollment,
  );
}

function getSearchFromPath(path?: string) {
  if (!path) {
    return '';
  }

  const queryStartIndex = path.indexOf('?');
  if (queryStartIndex === -1) {
    return '';
  }

  const hashStartIndex = path.indexOf('#', queryStartIndex);
  return path.slice(
    queryStartIndex + 1,
    hashStartIndex === -1 ? undefined : hashStartIndex,
  );
}

function hasUserInitiatedHdEnrollmentParam(search?: string) {
  const normalizedSearch = search?.startsWith('?') ? search.slice(1) : search;
  return new URLSearchParams(normalizedSearch || '').get(USER_INITIATED_HD_ENROLLMENT_PARAM) === 'true';
}

export function isUserInitiatedHomeDepotEnrollment({
  state,
  search,
  redirectPath,
}: {
  state?: unknown;
  search?: string;
  redirectPath?: string;
}) {
  return getUserInitiatedHdEnrollmentFromState(state) ||
    hasUserInitiatedHdEnrollmentParam(search) ||
    hasUserInitiatedHdEnrollmentParam(getSearchFromPath(redirectPath));
}

export function shouldAutoOpenHomeDepotEnrollModal(search: string) {
  const normalizedSearch = search.startsWith('?') ? search.slice(1) : search;
  return new URLSearchParams(normalizedSearch).get(HD_ENROLL_MODAL_PARAM) === 'true';
}

export function addUserInitiatedHomeDepotEnrollmentParam(path: string) {
  const hashStartIndex = path.indexOf('#');
  const pathWithoutHash = hashStartIndex === -1 ? path : path.slice(0, hashStartIndex);
  const hash = hashStartIndex === -1 ? '' : path.slice(hashStartIndex);
  const queryStartIndex = pathWithoutHash.indexOf('?');
  const pathname = queryStartIndex === -1 ?
    pathWithoutHash :
    pathWithoutHash.slice(0, queryStartIndex);
  const search = queryStartIndex === -1 ? '' : pathWithoutHash.slice(queryStartIndex + 1);
  const params = new URLSearchParams(search);
  params.set(USER_INITIATED_HD_ENROLLMENT_PARAM, 'true');

  return `${pathname}?${params.toString()}${hash}`;
}

function parseLocalPath(path: string) {
  const trimmedPath = path.trim();

  if (!trimmedPath ||
    /^[a-z][a-z\d+\-.]*:/i.test(trimmedPath) ||
    trimmedPath.startsWith('//')) {
    return null;
  }

  try {
    return new URL(
      trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`,
      localPathBaseUrl,
    );
  } catch {
    return null;
  }
}

function getSafeLocalPath(path: string) {
  const parsedPath = parseLocalPath(path);

  if (!parsedPath) {
    return undefined;
  }

  return `${parsedPath.pathname}${parsedPath.search}${parsedPath.hash}`;
}

function isHomePath(path: string) {
  const parsedPath = parseLocalPath(path);
  return parsedPath?.pathname === HOME_ROUTE;
}

export function isHomeDepotSetupPath(path: string) {
  const parsedPath = parseLocalPath(path);
  return Boolean(parsedPath && homeDepotSetupRoutes.has(parsedPath.pathname));
}

function getReturnPathFromHomeDepotSetupPath(path: string) {
  const parsedPath = parseLocalPath(path);

  if (!parsedPath || !isHomeDepotSetupPath(path)) {
    return undefined;
  }

  return parsedPath.searchParams.get('returnPath') || undefined;
}

function getFallbackReturnPath(fallbackPath: string) {
  const safeFallbackPath = getSafeLocalPath(fallbackPath) || PARTNERS_ROUTE;
  return isHomePath(safeFallbackPath) ? PARTNERS_ROUTE : safeFallbackPath;
}

function resolveNestedHomeDepotSetupReturnPath({
  redirectPath,
  fallbackPath,
  visitedPaths,
}: {
  redirectPath?: string;
  fallbackPath: string;
  visitedPaths: Set<string>;
}): string {
  if (!redirectPath) {
    return fallbackPath;
  }

  const safeRedirectPath = getSafeLocalPath(redirectPath);

  if (!safeRedirectPath || visitedPaths.has(safeRedirectPath)) {
    return fallbackPath;
  }

  if (isHomePath(safeRedirectPath)) {
    return PARTNERS_ROUTE;
  }

  if (isHomeDepotSetupPath(safeRedirectPath)) {
    visitedPaths.add(safeRedirectPath);

    return resolveNestedHomeDepotSetupReturnPath({
      redirectPath: getReturnPathFromHomeDepotSetupPath(safeRedirectPath),
      fallbackPath,
      visitedPaths,
    });
  }

  return safeRedirectPath;
}

export function resolveHomeDepotSetupReturnPath({
  redirectPath,
  fallbackPath = PARTNERS_ROUTE,
}: {
  redirectPath?: string;
  fallbackPath?: string;
}): string {
  const safeFallbackPath = getFallbackReturnPath(fallbackPath);
  return resolveNestedHomeDepotSetupReturnPath({
    redirectPath,
    fallbackPath: safeFallbackPath,
    visitedPaths: new Set(),
  });
}
